import { isVercelDeploymentHost } from "./creatorHost";

function isBrowsableOwnedHostname(hostname: string): boolean {
  const h = String(hostname || "").toLowerCase();
  return (
    h === "browsable.ai" ||
    h === "www.browsable.ai" ||
    h === "browsable.bio" ||
    h === "www.browsable.bio" ||
    h.endsWith(".browsable.ai") ||
    h.endsWith(".browsable.bio") ||
    h === "searchbubble.com" ||
    h === "www.searchbubble.com" ||
    h.endsWith(".searchbubble.com")
  );
}

/** Rewrite absolute browsable/searchbubble URLs to a same-origin path. */
export function rewriteBrowsableAbsoluteToPath(destination: string): string | null {
  const trimmed = String(destination || "").trim();
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) return null;
  try {
    const url = new URL(trimmed, typeof window !== "undefined" ? window.location.origin : undefined);
    if (!isBrowsableOwnedHostname(url.hostname)) return null;
    if (isVercelDeploymentHost(url.hostname)) return null;
    return `${url.pathname}${url.search}${url.hash}` || "/";
  } catch {
    return null;
  }
}

let installed = false;

/**
 * Hard stay-on-host for *.vercel.app preview deploys.
 * Intercepts location.assign/replace/href and window.open when the target is a
 * Browsable-owned host, rewriting to a same-origin path so testing never leaves Vercel.
 * No-op on production / custom domains.
 */
export function installVercelStayOnHostGuard(): void {
  if (typeof window === "undefined" || installed) return;
  if (!isVercelDeploymentHost()) return;
  installed = true;

  // Drop any stale custom-domain caches for this (or any) Vercel host.
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith("domain_mapping_slug_v1:")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }

  const rewriteOrPassthrough = (url: string): string => {
    const rewritten = rewriteBrowsableAbsoluteToPath(url);
    if (rewritten == null) return url;
    // Same-origin absolute so assign/replace/href still work.
    return `${window.location.origin}${rewritten}`;
  };

  try {
    const originalAssign = window.location.assign.bind(window.location);
    window.location.assign = ((url: string | URL) => {
      originalAssign(rewriteOrPassthrough(String(url)));
    }) as typeof window.location.assign;
  } catch {
    // ignore (some browsers freeze Location methods)
  }

  try {
    const originalReplace = window.location.replace.bind(window.location);
    window.location.replace = ((url: string | URL) => {
      originalReplace(rewriteOrPassthrough(String(url)));
    }) as typeof window.location.replace;
  } catch {
    // ignore
  }

  try {
    const hrefDesc = Object.getOwnPropertyDescriptor(Location.prototype, "href");
    if (hrefDesc?.set && hrefDesc?.get) {
      Object.defineProperty(Location.prototype, "href", {
        configurable: true,
        enumerable: hrefDesc.enumerable,
        get() {
          return hrefDesc.get!.call(this);
        },
        set(value: string) {
          hrefDesc.set!.call(this, rewriteOrPassthrough(String(value)));
        },
      });
    }
  } catch {
    // ignore
  }

  try {
    const originalOpen = window.open.bind(window);
    window.open = ((url?: string | URL, target?: string, features?: string) => {
      if (url == null || url === "") {
        return originalOpen(url, target, features);
      }
      return originalOpen(rewriteOrPassthrough(String(url)), target, features);
    }) as typeof window.open;
  } catch {
    // ignore
  }

  // Chromium Navigation API — cancel cross-origin browsable navigations.
  try {
    const nav = (window as any).navigation;
    if (nav && typeof nav.addEventListener === "function") {
      nav.addEventListener("navigate", (event: any) => {
        try {
          if (!event?.canIntercept || event.hashChange || event.downloadRequest) return;
          const dest = String(event?.destination?.url || "");
          const rewritten = rewriteBrowsableAbsoluteToPath(dest);
          if (rewritten == null) return;
          event.intercept({
            handler() {
              window.history.pushState(null, "", rewritten);
              window.dispatchEvent(new PopStateEvent("popstate"));
            },
          });
        } catch {
          // ignore
        }
      });
    }
  } catch {
    // ignore
  }
}
